import { get, post } from '@/shared/api';

import type {
  ArchiveNotificationsRequest,
  GetNotificationsRequest,
  MarkNotificationsReadRequest,
  NotificationActionResponse,
  NotificationApiItem,
  NotificationEntry,
  NotificationsApiResponse,
  NotificationsResponse,
} from '../../domain/types';

export interface NotificationDatasource {
  getNotifications(request: GetNotificationsRequest): Promise<NotificationsResponse>;
  getUnreadCount(): Promise<number>;
  markRead(request: MarkNotificationsReadRequest): Promise<NotificationActionResponse>;
  archive(request: ArchiveNotificationsRequest): Promise<NotificationActionResponse>;
}

function mapItem(item: NotificationApiItem): NotificationEntry {
  return {
    id: item.id,
    notificationId: item.notification_id,
    type: item.type,
    category: item.category,
    title: item.title,
    message: item.message,
    data: item.data,
    readAt: item.read_at,
    archivedAt: item.archived_at,
    createdAt: item.created_at,
  };
}

export class NotificationDatasourceImpl implements NotificationDatasource {
  async getNotifications(request: GetNotificationsRequest): Promise<NotificationsResponse> {
    const response = await post<NotificationsApiResponse>('/app/notification/paginated', {
      page: request.page,
      per_page: request.perPage ?? 10,
      unread_only: request.unreadOnly ?? false,
      include_archived: request.includeArchived ?? false,
    });
    return { items: response.notifications.map(mapItem), total: response.total, page: response.page, pages: response.pages };
  }

  async getUnreadCount(): Promise<number> {
    const response = await get<{ unread_count?: number; count?: number }>('/app/notification/unread-count');
    return response.unread_count ?? response.count ?? 0;
  }

  async markRead(request: MarkNotificationsReadRequest): Promise<NotificationActionResponse> {
    return post<NotificationActionResponse>('/app/notification/read', request.notificationIds ? { notification_ids: request.notificationIds } : {});
  }

  async archive(request: ArchiveNotificationsRequest): Promise<NotificationActionResponse> {
    return post<NotificationActionResponse>('/app/notification/archive', { notification_ids: request.notificationIds });
  }
}

export const notificationDatasource: NotificationDatasource = new NotificationDatasourceImpl();
